import React, { useEffect, useState } from "react";
import { Card } from "react-bootstrap";
import { FaUsers } from "react-icons/fa";
import { fetchClientTeamDetails } from "../../api/overviewApi";
import { useDataCache } from "../../contexts/DataCacheContext";

const roleColors = {
  "Project Manager": { bg: "rgba(59, 130, 246, 0.15)", text: "#3b82f6" },
  "Sales Person": { bg: "rgba(16, 185, 129, 0.15)", text: "#10b981" },
  "No Team Details": { bg: "rgba(148, 163, 184, 0.15)", text: "#94a3b8" },
};

const ResourceTable = ({ clientId }) => {
  const { getCachedData, setCachedData } = useDataCache();
  const [teamDetails, setTeamDetails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clientName, setClientName] = useState("");
  const isMobile = window.innerWidth <= 992;

  useEffect(() => {
    if (!clientId) {
      setTeamDetails([]);
      setClientName("");
      setLoading(false);
      return;
    }

    const cacheKey = "client_team_details";
    const cached = getCachedData(cacheKey, clientId);

    if (cached !== undefined) {
      setTeamDetails(cached.team_details || []);
      setClientName(cached.client_name || "");
      setLoading(false);
      return;
    }

    setLoading(true);
    fetchClientTeamDetails(clientId)
      .then((res) => {
        setTeamDetails(res.team_details || []);
        setClientName(res.client_name || "");
        setCachedData(cacheKey, res, clientId);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Failed to fetch team details:", error);
        setTeamDetails([]);
        setClientName("");
        setLoading(false);
      });
  }, [clientId, getCachedData, setCachedData]);

  const getRoleColor = (role) => {
    const roleKey = role || "No Team Details";
    return roleColors[roleKey] || roleColors["No Team Details"];
  };

  if (loading) {
    return (
      <Card className="shadow-sm" style={{ borderRadius: 16 }}>
        <Card.Body>
          <div style={{ minHeight: "400px", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <p>Loading...</p>
          </div>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className="shadow-sm" style={{ borderRadius: 16, background: "var(--color-bg-card)" }}>
      <Card.Body>
        <h6 className="fw-bold mb-3 mt-4 d-flex align-items-center gap-2" style={{ color: "var(--color-text)" }}>
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: 10,
              background: "rgba(59,130,246,0.15)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <FaUsers size={16} color="#3b82f6" />
          </div>
          Client Team Details
        </h6>

        <hr style={{ marginBottom: "0.25rem", marginTop: "0.5rem", borderColor: "var(--color-border)" }} />

        {isMobile ? (
          <div style={{ maxHeight: "550px", overflowY: "auto" }}>
            {teamDetails.length === 0 ? (
              <div style={{ textAlign: "center", padding: "40px", color: "var(--color-text-light)" }}>
                {clientId ? "No team details found for this client" : "Select a client to view team details"}
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                {teamDetails.map((member, index) => (
                  <div
                    key={index}
                    style={{
                      padding: "16px",
                      borderRadius: 12,
                      background: "var(--color-bg-card)",
                      border: "1px solid var(--color-border)",
                      boxShadow: "var(--shadow-sm)",
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
                      <span style={{ fontSize: "0.75rem", fontWeight: 600, color: "var(--color-text-light)" }}>
                        #{index + 1}
                      </span>
                      <span
                        style={{
                          background: getRoleColor(member.role).bg,
                          color: getRoleColor(member.role).text,
                          padding: "4px 10px",
                          borderRadius: 8,
                          fontSize: 11,
                          fontWeight: 600,
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                        }}
                      >
                        {member.role || "N/A"}
                      </span>
                    </div>
                    <div style={{ marginBottom: "8px" }}>
                      <div style={{ fontSize: "0.75rem", color: "var(--color-text-light)", marginBottom: "4px" }}>Name</div>
                      <div style={{ fontWeight: 500, color: "var(--color-text)", fontSize: "0.9rem" }}>
                        {member.name || "N/A"}
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: "0.75rem", color: "var(--color-text-light)", marginBottom: "4px" }}>Email</div>
                      {member.email && member.email !== "N/A" && member.email.trim() ? (
                        <a 
                          href={`mailto:${member.email}`}
                          style={{ 
                            fontSize: "0.9rem", 
                            color: "var(--color-primary)",
                            textDecoration: "none",
                            cursor: "pointer",
                            wordBreak: "break-all"
                          }}
                          onMouseEnter={(e) => e.target.style.textDecoration = "underline"}
                          onMouseLeave={(e) => e.target.style.textDecoration = "none"}
                        >
                          {member.email}
                        </a>
                      ) : (
                        <span style={{ fontSize: "0.9rem", color: "var(--color-text-light)" }}>
                          {member.email || "N/A"}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div style={{ overflowX: "auto", marginTop: "-0.25rem", paddingTop: 0 }}>
            <div style={{ maxHeight: "550px", overflowY: "auto", overflowX: "hidden", marginTop: 0, paddingTop: 0 }}>
              <table className="table" style={{ marginBottom: 0, marginTop: 0, width: "100%", borderCollapse: "separate", borderSpacing: 0 }}>
              {!isMobile && (
                <thead className="resource-table-header" style={{ position: "sticky", top: 0, background: "var(--color-bg-card)", zIndex: 10 }}>
                  <tr style={{ borderBottom: "1px solid var(--color-border)" }}>
                    <th style={{ padding: "8px 16px", fontWeight: 600, fontSize: "0.875rem", color: "var(--color-text)", background: "var(--color-bg-card)", width: "60px", textAlign: "center" }}>S.No</th>
                    <th style={{ padding: "8px 16px", fontWeight: 600, fontSize: "0.875rem", color: "var(--color-text)", background: "var(--color-bg-card)" }}>Role</th>
                    <th style={{ padding: "8px 16px", fontWeight: 600, fontSize: "0.875rem", color: "var(--color-text)", background: "var(--color-bg-card)" }}>Name</th>
                    <th style={{ padding: "8px 16px", fontWeight: 600, fontSize: "0.875rem", color: "var(--color-text)", background: "var(--color-bg-card)" }}>Email</th>
                  </tr>
                </thead>
              )}

              <tbody>
                {teamDetails.length === 0 ? (
                  <tr>
                    <td colSpan={isMobile ? 1 : 4} style={{ textAlign: "center", padding: "40px", color: "var(--color-text-light)" }}>
                      {clientId ? "No team details found for this client" : "Select a client to view team details"}
                    </td>
                  </tr>
                ) : (
                  teamDetails.map((member, index) => (
                    <tr 
                      key={index} 
                      style={{ 
                        verticalAlign: "middle", 
                        borderBottom: "1px solid var(--color-border)",
                        background: index % 2 === 0 ? "transparent" : "var(--color-bg)",
                        transition: "background 0.2s ease"
                      }} 
                      className="py-2"
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = "var(--color-primary-light)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = index % 2 === 0 ? "transparent" : "var(--color-bg)";
                      }}
                    >
                      {!isMobile && (
                        <td style={{ textAlign: "center", padding: "8px 16px", color: "var(--color-text)", fontWeight: 500, background: "transparent" }}>
                          {index + 1}
                        </td>
                      )}
                      <td style={{ padding: "8px 16px", background: "transparent" }}>
                        <span
                          style={{
                            background: getRoleColor(member.role).bg,
                            color: getRoleColor(member.role).text,
                            padding: "5px 12px",
                            borderRadius: 12,
                            fontSize: 12,
                            fontWeight: 600,
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                            display: "inline-block",
                          }}
                        >
                          {member.role || "N/A"}
                        </span>
                      </td>

                      <td style={{ padding: "8px 16px", background: "transparent" }}>
                        <div
                          style={{
                            fontWeight: 500,
                            color: "var(--color-text)",
                          }}
                        >
                          {member.name || "N/A"}
                        </div>
                      </td>

                      <td style={{ padding: "8px 16px", background: "transparent" }}>
                        {member.email && member.email !== "N/A" && member.email.trim() ? (
                          <a 
                            href={`mailto:${member.email}`}
                            style={{ 
                              fontSize: "0.9rem", 
                              color: "var(--color-primary)",
                              textDecoration: "none",
                              cursor: "pointer"
                            }}
                            onMouseEnter={(e) => e.target.style.textDecoration = "underline"}
                            onMouseLeave={(e) => e.target.style.textDecoration = "none"}
                          >
                            {member.email}
                          </a>
                        ) : (
                          <span style={{ fontSize: "0.9rem", color: "var(--color-text-light)" }}>
                            {member.email || "N/A"}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
              </table>
            </div>
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default ResourceTable;
